/**
 * NotificationBell (NOTIFY) — the global header bell. Shows the count of unread
 * notifications for the signed-in user (conversations assigned to you, @mentions
 * in internal notes), a dropdown list, and click-through to the conversation.
 * Live via realtime + a 60s poll backstop.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, UserPlus, AtSign } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";

interface Notif {
  id: string;
  type: string;
  conversation_key: string | null;
  custno: string | null;
  title: string;
  actor: string | null;
  created_at: string;
  read_at: string | null;
}

function timeAgo(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: email } = useQuery({
    queryKey: ["me_email"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => (await supabase.auth.getUser()).data?.user?.email ?? null,
  });
  const { data: notifs } = useQuery({
    queryKey: ["notifications", email],
    enabled: !!email,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<Notif[]> => {
      const { data, error } = await supabase
        .from("notification")
        .select("*")
        .eq("recipient", email!)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as Notif[];
    },
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useRealtimeRefresh(["notification"], [["notifications"]]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const list = notifs ?? [];
  const unread = list.filter((n) => !n.read_at).length;

  const markRead = async (ids: string[]) => {
    if (!ids.length) return;
    await supabase.from("notification").update({ read_at: new Date().toISOString() }).in("id", ids);
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  const onClick = async (n: Notif) => {
    if (!n.read_at) await markRead([n.id]);
    setOpen(false);
    if (n.conversation_key) navigate(`/inbox?c=${encodeURIComponent(n.conversation_key)}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-glass="panel" className="absolute right-0 z-50 mt-1.5 w-80 overflow-hidden rounded-2xl">
          <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
            <span className="text-xs font-semibold text-slate-900">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead(list.filter((n) => !n.read_at).map((n) => n.id))}
                className="text-[11px] font-medium text-brand-600 transition-colors hover:text-brand-700"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {list.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-slate-400">You're all caught up.</div>
            ) : (
              list.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onClick(n)}
                  className={cn(
                    "flex w-full items-start gap-2.5 border-b border-hairline px-3 py-2.5 text-left transition-colors hover:bg-slate-50",
                    !n.read_at && "bg-brand-50/40",
                  )}
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    {n.type === "mention" ? <AtSign className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-slate-800">{n.title}</span>
                    <span className="block text-[11px] text-slate-400">
                      {n.actor ? `${n.actor.split("@")[0]} · ` : ""}
                      {timeAgo(n.created_at)}
                    </span>
                  </span>
                  {!n.read_at && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
